import React, { useState, useEffect } from 'react';
import { NovelCharacter, Chapter } from '../../services/novelService';
import './MetadataGenerationModal.css';

interface MetadataGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  fieldType: 'plots' | 'characters' | 'settings' | 'themes';
  chapters: Chapter[];
  currentData?: any[];
  onGenerate: (chapterIds: string[]) => Promise<void>;
  onAccept: (data: any[]) => void;
  generatedData: any[] | null;
  isGenerating: boolean;
  error: string | null;
}

const MetadataGenerationModal: React.FC<MetadataGenerationModalProps> = ({
  isOpen,
  onClose,
  fieldType,
  chapters,
  currentData = [],
  onGenerate,
  onAccept,
  generatedData,
  isGenerating,
  error
}) => {
  const [selectedChapterIds, setSelectedChapterIds] = useState<string[]>(
    chapters.map(ch => ch.chapter_id)
  );
  const [editedData, setEditedData] = useState<any[] | null>(null);
  const [viewMode, setViewMode] = useState<'selection' | 'preview'>('selection');
  
  // Merge interface state
  const [selectedExisting, setSelectedExisting] = useState<Set<number>>(new Set());
  const [selectedGenerated, setSelectedGenerated] = useState<Set<number>>(new Set());

  // Initialize all items as selected by default when data arrives
  useEffect(() => {
    if (generatedData && viewMode === 'preview') {
      setSelectedGenerated(new Set(generatedData.map((_, i) => i)));
    }
  }, [generatedData, viewMode]);

  useEffect(() => {
    if (currentData && viewMode === 'preview') {
      setSelectedExisting(new Set(currentData.map((_, i) => i)));
    }
  }, [currentData, viewMode]);

  if (!isOpen) return null;

  const handleToggleChapter = (chapterId: string) => {
    setSelectedChapterIds(prev =>
      prev.includes(chapterId)
        ? prev.filter(id => id !== chapterId)
        : [...prev, chapterId]
    );
  };

  const handleSelectAll = () => {
    if (selectedChapterIds.length === chapters.length) {
      setSelectedChapterIds([]);
    } else {
      setSelectedChapterIds(chapters.map(ch => ch.chapter_id));
    }
  };

  const handleGenerate = async () => {
    if (selectedChapterIds.length === 0) return;
    setEditedData(null);
    await onGenerate(selectedChapterIds);
    setViewMode('preview');
  };

  const handleAcceptAll = () => {
    // Get the data to work with (edited or generated)
    const dataToMerge = editedData || generatedData || [];
    
    // Merge selected existing + selected generated plots
    const mergedData: any[] = [];
    
    // First, add selected existing items
    selectedExisting.forEach(index => {
      if (index < currentData.length) {
        mergedData.push(currentData[index]);
      }
    });
    
    // Then, add selected generated items
    selectedGenerated.forEach(index => {
      if (index < dataToMerge.length) {
        mergedData.push(dataToMerge[index]);
      }
    });
    
    onAccept(mergedData);
    handleClose();
  };

  const handleClose = () => {
    setViewMode('selection');
    setEditedData(null);
    setSelectedExisting(new Set());
    setSelectedGenerated(new Set());
    onClose();
  };

  const handleEditItem = (index: number, field: string, value: any) => {
    const dataToEdit = editedData || generatedData || [];
    const newData = [...dataToEdit];
    
    if (fieldType === 'characters') {
      newData[index] = { ...newData[index], [field]: value };
    } else {
      newData[index] = value;
    }
    
    setEditedData(newData);
  };

  const handleToggleExisting = (index: number) => {
    const newSet = new Set(selectedExisting);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedExisting(newSet);
  };

  const handleToggleGenerated = (index: number) => {
    const newSet = new Set(selectedGenerated);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedGenerated(newSet);
  };

  const handleToggleAllExisting = () => {
    if (selectedExisting.size === currentData.length) {
      setSelectedExisting(new Set());
    } else {
      setSelectedExisting(new Set(currentData.map((_, i) => i)));
    }
  };

  const handleToggleAllGenerated = () => {
    const dataToShow = editedData || generatedData || [];
    if (selectedGenerated.size === dataToShow.length) {
      setSelectedGenerated(new Set());
    } else {
      setSelectedGenerated(new Set(dataToShow.map((_, i) => i)));
    }
  };

  const handleRemoveItem = (index: number) => {
    const dataToEdit = editedData || generatedData || [];
    const newData = dataToEdit.filter((_, i) => i !== index);
    setEditedData(newData);
  };

  const getFieldLabel = () => {
    const labels: Record<string, string> = {
      plots: 'Plots',
      characters: 'Characters',
      settings: 'Settings',
      themes: 'Themes'
    };
    return labels[fieldType] || fieldType;
  };

  const renderCurrentData = () => {
    if (!currentData || currentData.length === 0) {
      return <p className="metadata-gen-modal-empty">No existing {getFieldLabel().toLowerCase()}</p>;
    }

    return (
      <div className="metadata-gen-modal-list">
        {currentData.map((item, index) => (
          <div key={index} className={`metadata-gen-modal-item metadata-gen-modal-item-current ${selectedExisting.has(index) ? 'selected' : ''}`}>
            <div className="metadata-gen-modal-item-checkbox-wrapper">
              <input
                type="checkbox"
                checked={selectedExisting.has(index)}
                onChange={() => handleToggleExisting(index)}
                className="metadata-gen-modal-checkbox"
              />
              <div className="metadata-gen-modal-item-content">
                {fieldType === 'characters' ? (
                  <>
                    <div className="metadata-gen-modal-char-name">{item.name}</div>
                    <div className="metadata-gen-modal-char-role">{item.role}</div>
                    <div className="metadata-gen-modal-char-desc">{item.description}</div>
                  </>
                ) : (
                  <div className="metadata-gen-modal-text">{item}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderGeneratedData = () => {
    const dataToShow = editedData || generatedData || [];
    
    if (dataToShow.length === 0) {
      return <p className="metadata-gen-modal-empty">No data generated</p>;
    }

    return (
      <div className="metadata-gen-modal-list">
        {dataToShow.map((item, index) => (
          <div key={index} className={`metadata-gen-modal-item metadata-gen-modal-item-generated ${selectedGenerated.has(index) ? 'selected' : ''}`}>
            <div className="metadata-gen-modal-item-checkbox-wrapper">
              <input
                type="checkbox"
                checked={selectedGenerated.has(index)}
                onChange={() => handleToggleGenerated(index)}
                className="metadata-gen-modal-checkbox"
              />
              <div className="metadata-gen-modal-item-content-editable">
                {fieldType === 'characters' ? (
                  <>
                    <div className="metadata-gen-modal-item-header">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleEditItem(index, 'name', e.target.value)}
                        className="metadata-gen-modal-input"
                        placeholder="Character name"
                      />
                      <button
                        className="metadata-gen-modal-remove-btn"
                        onClick={() => handleRemoveItem(index)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                    <select
                      value={item.role}
                      onChange={(e) => handleEditItem(index, 'role', e.target.value)}
                      className="metadata-gen-modal-select"
                    >
                      <option value="protagonist">Protagonist</option>
                      <option value="antagonist">Antagonist</option>
                      <option value="supporting">Supporting</option>
                      <option value="other">Other</option>
                    </select>
                    <textarea
                      value={item.description}
                      onChange={(e) => handleEditItem(index, 'description', e.target.value)}
                      className="metadata-gen-modal-textarea"
                      placeholder="Description"
                      rows={3}
                    />
                  </>
                ) : (
                  <div className="metadata-gen-modal-item-editable-wrapper">
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      data-placeholder={getFieldLabel().slice(0, -1)}
                      className={`metadata-gen-modal-editable ${!item || item.trim() === '' ? 'empty' : ''}`}
                      onBlur={(e) => handleEditItem(index, '', e.currentTarget.textContent || '')}
                      dangerouslySetInnerHTML={{ __html: item || '' }}
                    />
                    <button
                      className="metadata-gen-modal-remove-btn"
                      onClick={() => handleRemoveItem(index)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="metadata-gen-modal-overlay" onClick={handleClose}>
      <div className="metadata-gen-modal" onClick={(e) => e.stopPropagation()}>
        <div className="metadata-gen-modal-header">
          <h3 className="metadata-gen-modal-title">
            Generate {getFieldLabel()} from Chapters
          </h3>
          <button className="metadata-gen-modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="metadata-gen-modal-body">
          {viewMode === 'selection' ? (
            <>
              {/* Chapter Selection */}
              <div className="metadata-gen-modal-section">
                <div className="metadata-gen-modal-section-header">
                  <label className="metadata-gen-modal-label">
                    Select Chapters to Analyze
                  </label>
                  {chapters.length > 1 && (
                    <button
                      className="metadata-gen-modal-select-all"
                      onClick={handleSelectAll}
                    >
                      {selectedChapterIds.length === chapters.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>

                {chapters.length === 0 ? (
                  <p className="metadata-gen-modal-empty">No chapters available</p>
                ) : (
                  <div className="metadata-gen-modal-chapters">
                    {chapters.map((chapter) => (
                      <label key={chapter.chapter_id} className="metadata-gen-modal-chapter-item">
                        <input
                          type="checkbox"
                          checked={selectedChapterIds.includes(chapter.chapter_id)}
                          onChange={() => handleToggleChapter(chapter.chapter_id)}
                        />
                        <span className="metadata-gen-modal-chapter-title">
                          {chapter.order}. {chapter.title}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {selectedChapterIds.length > 0 && (
                  <div className="metadata-gen-modal-info">
                    {selectedChapterIds.length} chapter{selectedChapterIds.length > 1 ? 's' : ''} selected
                    {selectedChapterIds.length > 5 && (
                      <span className="metadata-gen-modal-warning">
                        {' '}• Large selection may take longer to analyze
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Preview */}
              {error && (
                <div className="metadata-gen-modal-error">
                  {error}
                </div>
              )}

              {isGenerating ? (
                <div className="metadata-gen-modal-loading">
                  <div className="metadata-gen-modal-spinner"></div>
                  <p>Analyzing chapters with AI...</p>
                </div>
              ) : (
                <>
                  <div className="metadata-gen-modal-merge-info">
                    <span className="metadata-gen-modal-merge-count">
                      {selectedExisting.size} existing + {selectedGenerated.size} new = {selectedExisting.size + selectedGenerated.size} total
                    </span>
                  </div>
                  <div className="metadata-gen-modal-comparison">
                    <div className="metadata-gen-modal-column">
                      <div className="metadata-gen-modal-column-header">
                        <h4 className="metadata-gen-modal-column-title">Current</h4>
                        {currentData && currentData.length > 0 && (
                          <button
                            className="metadata-gen-modal-toggle-all"
                            onClick={handleToggleAllExisting}
                          >
                            {selectedExisting.size === currentData.length ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                      </div>
                      {renderCurrentData()}
                    </div>

                    <div className="metadata-gen-modal-divider"></div>

                    <div className="metadata-gen-modal-column">
                      <div className="metadata-gen-modal-column-header">
                        <h4 className="metadata-gen-modal-column-title">Generated (Editable)</h4>
                        {(editedData || generatedData) && (editedData || generatedData || []).length > 0 && (
                          <button
                            className="metadata-gen-modal-toggle-all"
                            onClick={handleToggleAllGenerated}
                          >
                            {selectedGenerated.size === (editedData || generatedData || []).length ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                      </div>
                      {renderGeneratedData()}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="metadata-gen-modal-footer">
          {viewMode === 'selection' ? (
            <>
              <button
                className="metadata-gen-modal-btn metadata-gen-modal-btn-secondary"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                className="metadata-gen-modal-btn metadata-gen-modal-btn-primary"
                onClick={handleGenerate}
                disabled={selectedChapterIds.length === 0 || isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            </>
          ) : (
            <>
              <button
                className="metadata-gen-modal-btn metadata-gen-modal-btn-secondary"
                onClick={() => setViewMode('selection')}
                disabled={isGenerating}
              >
                ← Back
              </button>
              <button
                className="metadata-gen-modal-btn metadata-gen-modal-btn-primary"
                onClick={handleAcceptAll}
                disabled={isGenerating || (selectedExisting.size === 0 && selectedGenerated.size === 0)}
              >
                {selectedExisting.size === 0 && selectedGenerated.size === 0
                  ? 'Select items to merge'
                  : `Merge ${selectedExisting.size + selectedGenerated.size} ${getFieldLabel().toLowerCase()}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetadataGenerationModal;

